'use client';

import {type Credit} from '#/cms/schemas/credit.schema';
import {type MoreSection} from '#/cms/schemas/more-sections.schema';
import {type ThemeColors} from '#/cms/schemas/theme-colors.schema';
import Box from '#/features/components/Box';
import {PepIcon} from '#/features/components/PepIcon';
import Select from '#/features/components/Select';
import {Heading2, Heading1, SmallText, BodyText} from '#/features/components/Text';
import {ColorThemeControl} from '#/styles/color-theme-control';
import classNames from 'classnames';
import Link from 'next/link';
import {useState} from 'react';
import {useUiStrings} from '#/features/store/uiStrings';
import useAnalytics, {AnalyticsEventAction, AnalyticsEventCategory} from '#/features/analytics/useAnalytics';
import {type FeedbackInfo} from '#/cms/schemas/content.schema';

type CombinedSection = MoreSection | (Pick<MoreSection, 'id' | 'title'> & {credits: Credit[]});

type Props = {
  moreSections: MoreSection[];
  credits: Credit[];
  themeColors: ThemeColors;
  feedback?: FeedbackInfo;
  hasDarkMode: boolean;
};

export function MorePanel(props: Props) {
  const {moreSections, credits, themeColors, hasDarkMode, feedback} = props;
  const {registerEvent} = useAnalytics();
  const uiStrings = useUiStrings();

  const creditsSection = {
    id: 'credits',
    title: uiStrings.more.creditsTitle,
    credits: credits,
  };

  const feedbackIsValid =
    feedback &&
    (!!(feedback?.feedbackType === 'formLink' && feedback.formLinkUrl) ||
      !!(feedback?.feedbackType === 'email' && feedback.feedbackEmail));

  const feedbackSection = feedbackIsValid
    ? {
        id: 'feedback',
        title: uiStrings.more.feedback.title,
        credits: [
          {
            id: 'feedback',
            creditType: 'link',
            role: null,
            shortDescription: null,
            longDescription: null,
            name:
              feedback.feedbackType === 'formLink'
                ? uiStrings.more.feedback.feedbackLinkLabel
                : uiStrings.more.feedback.feedbackEmailLabel,
            link: feedback.feedbackType === 'formLink' ? feedback.formLinkUrl : `mailto:${feedback.feedbackEmail}`,
          },
        ],
      }
    : null;

  const allSections = [creditsSection, ...moreSections, ...(feedbackSection ? [feedbackSection] : [])];

  const sectionOptions = [creditsSection, ...moreSections].map((section: MoreSection) => ({
    id: section.id,
    title: section.title ?? section.id,
  }));

  const [selectedSection, setSelectedSection] = useState(allSections[0]);
  const handleSectionChange = (section: CombinedSection) => {
    setSelectedSection(allSections.find(s => s.id === section.id));
    registerEvent({
      category: AnalyticsEventCategory.MoreSection,
      action: AnalyticsEventAction.Click,
      label: section.title ?? section.id,
      id: section.id,
    });
  };

  return (
    <div className="grow">
      <div className="flex h-full min-h-fit flex-row md:h-dvh mobile:flex-col">
        <main
          className={classNames(
            'grid h-full w-full grid-rows-[min-content_1fr_min-content] md:grid-cols-3 md:grid-rows-[1fr_min-content]'
          )}
        >
          {/* more (left col) */}
          <section className="flex h-full flex-col gap-6 px-6 scrollbar md:row-end-2 md:p-0">
            <header className="p-6 md:pt-5">
              <Heading2>{uiStrings.more.title}</Heading2>
            </header>
            {/* scrollable section container */}
            <div className="relative md:overflow-y-auto md:scrollbar">
              <div className="sticky top-0 -z-10 h-[1px] w-full bg-midground3 md:z-20" />
              <div className="relative -top-[1px] -z-10 h-[1px] w-full bg-background md:z-40" />
              <div className="z-20 flex flex-col gap-6 px-6 md:px-8">
                {selectedSection && (
                  <>
                    <Select
                      className={'md:absolute md:hidden'}
                      value={{id: selectedSection.id, title: selectedSection.title ?? selectedSection.id}}
                      onChange={handleSectionChange}
                      title={selectedSection.title}
                      options={sectionOptions}
                      border={'primary'}
                    />
                    <div className="absolute hidden flex-col gap-6 md:visible md:relative md:flex">
                      {allSections.map(section => (
                        <button key={section.id} onClick={() => handleSectionChange(section)}>
                          <div
                            className={classNames(
                              'flex justify-between rounded-3xl border p-4',
                              section.id === selectedSection.id ? 'border-primary' : 'border-midground3'
                            )}
                          >
                            <BodyText>{section.title}</BodyText>
                            <PepIcon iconName={'ChevronRight'} />
                          </div>
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </section>

          {/* selected Section (right column) */}
          <section className="flex flex-col justify-start gap-6 p-6 scrollbar md:col-span-2 md:row-span-3 md:h-dvh md:border-l md:border-midground3 md:p-0">
            {selectedSection && (
              <>
                <header className="p-6">
                  <Heading1>{selectedSection.title ?? ''}</Heading1>
                </header>
                {/* scrollable section container */}
                <div className="relative overflow-y-auto scrollbar">
                  <div className="sticky top-0 -z-10 h-[1px] w-full bg-midground3 md:z-20" />
                  <div className="relative -top-[1px] -z-10 h-[1px] w-full bg-background md:z-40" />
                  {/* content */}
                  <div className="z-20 flex flex-col gap-6 px-6 md:px-8">
                    {'credits' in selectedSection ? (
                      selectedSection.credits.map(
                        ({link, creditType, id, role, name, shortDescription, longDescription}) =>
                          creditType === 'link' && link ? (
                            <Link key={id} target="_blank" href={link ?? ''}>
                              <Box key={id}>
                                <div className="flex justify-between">
                                  <div>
                                    {role && <SmallText>{role?.toUpperCase()}</SmallText>}
                                    <BodyText className="text-primary">{name}</BodyText>
                                  </div>
                                  <PepIcon iconName={'Hyperlink'} size="w-6" />
                                </div>
                              </Box>
                            </Link>
                          ) : (
                            <Box key={id}>
                              <SmallText>{role?.toUpperCase()}</SmallText>
                              <BodyText className="text-primary">{shortDescription ?? longDescription}</BodyText>
                            </Box>
                          )
                      )
                    ) : selectedSection.link ? (
                      <Link key={selectedSection.id} target="_blank" href={selectedSection.link ?? ''}>
                        <Box key={selectedSection.id}>
                          <div className="flex justify-between">
                            <div>
                              <BodyText className="text-primary">{selectedSection.link}</BodyText>
                            </div>
                            <PepIcon iconName={'Hyperlink'} size="w-6" />
                          </div>
                        </Box>
                      </Link>
                    ) : (
                      <Box>
                        <BodyText>{selectedSection.body}</BodyText>
                      </Box>
                    )}
                  </div>
                </div>
              </>
            )}
          </section>

          {/* Colour theme  control */}
          {hasDarkMode && (
            <section className="flex flex-col justify-end gap-3 border-t border-midground3 p-6 md:col-start-1 md:row-start-2 md:h-32 md:justify-center md:px-6 md:py-0">
              <SmallText className="font-bold">{uiStrings.more.themeTitle}</SmallText>
              <ColorThemeControl themeColor={themeColors} />
            </section>
          )}
        </main>
      </div>
    </div>
  );
}
